import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { Typography, Tabs } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid } from '@material-ui/core';

import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import PermissionsCard from '@portal/components/PermissionsCard/PermissionsCard';

import { getRouteStackPath } from '@portal/config/routes';
import { translate } from '@portal/services/i18n';

import { UserActions, UserImpersonationActions } from '@portal/actions';
import UserImpersonationApi from '@portal/repositories/userImpersonation';
import { PermissionsCardType } from '@portal/models/enumerators/cards';
import {
  cards,
  employees,
  payment,
  payslip,
  pix,
  transfer,
  myAccount,
} from '@portal/config/app.json';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import { ServiceFeature } from '@portal/models/enumerators/employees';
import useReduxState from '@portal/hooks/useReduxState';
import { useDispatch } from 'react-redux';

enum EditExternalUserTabs {
  PERMISSIONS = '1',
}

interface PermissionCardConfig {
  type: PermissionsCardType;
  titleKey: string;
  condition: boolean;
}

import './EditExternalUser.less';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import { useNavigation } from '@portal/hooks/useNavigation';

const { Text } = Typography;
const { TabPane } = Tabs;

const editExternalUserSchema = z.object({
  permissions: z
    .array(z.string())
    .min(
      1,
      translate(
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.VALIDATION.SELECT_AT_LEAST_ONE_PERMISSION'
      )
    ),
});

type EditExternalUserForm = z.infer<typeof editExternalUserSchema>;

const EditExternalUser = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<EditExternalUserTabs>(
    EditExternalUserTabs.PERMISSIONS
  );
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] =
    useState<models.UserImpersonationToAccess | null>(null);

  const {
    control,
    handleSubmit: handleFormSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditExternalUserForm>({
    resolver: zodResolver(editExternalUserSchema),
    defaultValues: {
      permissions: [],
    },
    mode: 'onChange',
  });

  const permissions = watch('permissions');

  const {
    user: { permissionsList, me },
  } = useReduxState();

  const hasMatchingPermissions = (type: PermissionsCardType) => {
    return permissionsList?.filter((item) => type === item.type).length > 0;
  };

  useEffect(() => {
    dispatch(UserActions.availablePermissions());
  }, [dispatch]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (id) {
        try {
          const userData = await UserImpersonationApi.getUserToAccess(id);
          setUserInfo(userData);
          setValue('permissions', userData.roles || []);
        } catch (error) {
          navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
        }
      }
    };

    fetchUserInfo();
  }, [id, setValue, history]);

  const handlePermissionChange = (permission: string) => {
    const currentPermissions = permissions || [];
    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter((p) => p !== permission)
      : [...currentPermissions, permission];
    setValue('permissions', newPermissions);
  };

  const allowEmployees = me?.account?.financialFeature?.some(
    (account) => account.serviceType === ServiceFeature.ALLOW_PAYMENT
  );

  const onSubmit = async (data: EditExternalUserForm) => {
    if (!id) return;

    setLoading(true);

    try {
      const currentPermissions = userInfo?.roles || [];
      const newPermissions = data.permissions;

      const permissionsToRemove = currentPermissions.filter(
        (p: string) => !newPermissions.includes(p)
      );
      const permissionsToAdd = newPermissions.filter(
        (p: string) => !currentPermissions.includes(p)
      );

      const updateBody: {
        removePermissions?: string[];
        addPermissions?: string[];
      } = {};

      if (permissionsToRemove.length > 0) {
        updateBody.removePermissions = permissionsToRemove;
      }

      if (permissionsToAdd.length > 0) {
        updateBody.addPermissions = permissionsToAdd;
      }

      if (Object.keys(updateBody).length > 0) {
        await dispatch(
          UserImpersonationActions.updateUserImpersonationPermissions(
            id,
            updateBody
          )
        );
      }

      navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'));
    } catch (error) {
      // Error is handled by the action
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = permissions?.length > 0;

  const permissionCardsConfig: PermissionCardConfig[] = [
    {
      type: PermissionsCardType.TRANSFER,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.TRANSFER',
      condition: true,
    },
    {
      type: PermissionsCardType.PIX,
      titleKey: 'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PIX',
      condition: pix.enabled,
    },
    {
      type: PermissionsCardType.BILLET,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.BILLET.TEXT',
      condition: myAccount.billetLimit,
    },
    {
      type: PermissionsCardType.TED,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.TED.TEXT',
      condition: transfer.ted,
    },
    {
      type: PermissionsCardType.P2P,
      titleKey: 'ENUMERATORS.TRANSACTION_TYPE.P2P.TEXT',
      condition: transfer.p2p,
    },
    {
      type: PermissionsCardType.CARDS,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.CARDS',
      condition: cards.enabled,
    },
    {
      type: PermissionsCardType.PROFILE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PROFILE',
      condition: true,
    },
    {
      type: PermissionsCardType.PAYABLE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYABLE',
      condition: payment.accountPayable,
    },
    {
      type: PermissionsCardType.RECEIVABLE,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.RECEIVABLE',
      condition: payment.accountReceivable,
    },
    {
      type: PermissionsCardType.PAYSLIP,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.PAYSLIP',
      condition:
        me.account.type === AccountTaxIdentifierType.CORPORATE &&
        !!payslip.enabled,
    },
    {
      type: PermissionsCardType.EMPLOYEES,
      titleKey:
        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.CARD_TYPES.EMPLOYEES',
      condition: employees.enabled && !!allowEmployees,
    },
  ];

  if (!userInfo) {
    return (
      <div className="edit-external-user-page">
        <div className="edit-external-user-page__loading">
          <ThemedText variant="body">
            {translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.LOADING'
            )}
          </ThemedText>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.BREADCRUMB.HOME'
            ),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.BREADCRUMB.MY_ACCOUNT'
            ),
            url: getRouteStackPath('PROFILE', 'MY_ACCOUNT'),
          },
          {
            active: false,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.BREADCRUMB.MULTI_ACCESS'
            ),
            url: getRouteStackPath('PROFILE', 'MULTI_ACCESS'),
          },
          {
            active: true,
            title: translate(
              'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.BREADCRUMB.EDIT_USER'
            ),
          },
        ]}
      />
      <div className="edit-external-user-page">
        <AdvancedPanelContentHeader
          title={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.TITLE'
          )}
          description={translate(
            'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.SUBTITLE'
          )}
          testId="edit-external-user-header"
          goBackAction={() => navigate.back()}
        />
        <div className="edit-external-user-page__content">
          <AdvancedCard testId="edit-external-user-card-user-info">
            <div className="edit-external-user-page__user-info">
              <ThemedText>
                {translate(
                  'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.USER_INFO'
                )}
                :
              </ThemedText>
              <ThemedText variant="body">
                {' '}
                {userInfo.targetUser?.name}
              </ThemedText>{' '}
              <ThemedText variant="body">
                ({userInfo.targetUser?.login})
              </ThemedText>
            </div>
          </AdvancedCard>
          <AdvancedCard testId="edit-external-user-card">
            <form onSubmit={handleFormSubmit(onSubmit)}>
              <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as EditExternalUserTabs)}
                className="edit-external-user-page__tabs"
                type="card"
              >
                <TabPane
                  tab={
                    <ThemedText>
                      <KeyOutlined />
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.TABS.PERMISSIONS.TITLE'
                      )}
                    </ThemedText>
                  }
                  key={EditExternalUserTabs.PERMISSIONS}
                >
                  <div className="edit-external-user-page__permissions">
                    <ThemedText className="edit-external-user-page__permissions__description">
                      {translate(
                        'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.PERMISSIONS_DESCRIPTION'
                      )}
                    </ThemedText>
                    <Controller
                      name="permissions"
                      control={control}
                      render={({ field }) => (
                        <Grid container spacing={2}>
                          {permissionCardsConfig
                            .filter(
                              (config) =>
                                config.condition &&
                                hasMatchingPermissions(config.type)
                            )
                            .map((config) => (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={4}
                                key={config.type}
                              >
                                <PermissionsCard
                                  type={config.type}
                                  title={translate(config.titleKey)}
                                  onChange={handlePermissionChange}
                                  selectedRoles={field.value || []}
                                />
                              </Grid>
                            ))}
                        </Grid>
                      )}
                    />
                    {errors.permissions ? (
                      <Text
                        type="danger"
                        className="edit-external-user-page__error"
                      >
                        {errors.permissions.message}
                      </Text>
                    ) : null}
                  </div>
                </TabPane>
              </Tabs>
              <div className="edit-external-user-page__actions">
                <AdvancedButton
                  text={translate(
                    'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.CANCEL_BUTTON'
                  )}
                  onClick={() =>
                    navigate(getRouteStackPath('PROFILE', 'MULTI_ACCESS'))
                  }
                  variant="outlined"
                  customStyles={{ marginRight: '16px' }}
                />
                <AdvancedButton
                  text={translate(
                    'PAGES.MY_ACCOUNT.MULTI_ACCESS.EDIT_EXTERNAL_USER.SAVE_BUTTON'
                  )}
                  type="submit"
                  disabled={!canSubmit}
                  loading={loading}
                />
              </div>
            </form>
          </AdvancedCard>
        </div>
      </div>
    </>
  );
};

export default EditExternalUser;
